import type { Message } from '../types';
import type { DisplayStrategy, StageKind, StageStatus } from './displayStrategy';
import { STAGE_ORDER } from './displayStrategy';
import { compactProjectTitle } from './messageDisplay';
import { getWorkflowStageGates } from './workflowStageGuards';
import { getEffectiveValidationReport, getRepairDisplayStatus, getRepairTimelineSummary, isRepairAppendixAuthoritative, isRepairOutcomeSuccess } from './repairDisplayState';
import { formatValidationRootCauseCount } from './validationRootCauseSummary';
import { looksLikePlcCode } from './messageTopic';
import { getDisplayCodeLineCount } from './codeDisplay';
import { nativeCompileConnector } from './presentation/plcTarget';
import { readBlockerLabel, readOutcome } from './tiaSituations';
import { deliveryValidationLabel, type DeliveryReview } from './deliveryValidationLabel';

export interface StageTimelineItem {
  id: string;
  kind: StageKind;
  label: string;
  status: StageStatus;
  summary?: string;
  /**
   * P0-1 (UI review 2026-07-23): full stage content for post-completion /
   * post-reload review. `summary` is a one-line preview; `detail` preserves
   * the complete stage output so earlier content never disappears after the
   * turn completes or the page reloads.
   */
  detail?: string;
  detailKind?: 'code' | 'text';
  meta?: string;
  isSafetyRelated: boolean;
  reviewRequired?: DeliveryReview;
}

const STAGE_RANK: Record<StageKind, number> = STAGE_ORDER.reduce(
  (acc, kind, index) => ({ ...acc, [kind]: index }),
  {} as Record<StageKind, number>,
);

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

export function latestPlanReview(message: Message): Message['planReview'] {
  const history = message.planProcessHistory || [];
  for (let index = history.length - 1; index >= 0; index -= 1) {
    if (history[index]?.planReview) return history[index].planReview;
  }
  return message.planReview || message.stageBuffers?.planReview?.artifact as Message['planReview'];
}

function hasPlanProcessStage(
  message: Message,
  predicate: (stage: string | undefined, item?: NonNullable<Message['planProcessHistory']>[number]) => boolean,
): boolean {
  return Boolean(message.planProcessHistory?.some(item => predicate(item.stage, item)));
}

function hasRequirementAnalysisArtifact(message: Message): boolean {
  return Boolean(
    hasText(message.planAnalysisReasoning)
      || hasText(message.stageBuffers?.requirementAnalysis?.text)
      || Boolean(message.stageBuffers?.requirementAnalysis?.artifact)
      || hasPlanProcessStage(
        message,
        (stage, item) => stage === 'requirement_analysis' && (hasText(item?.reasoning) || Boolean(item?.summary)),
      )
      || message.pipeline?.requirement,
  );
}

function hasAnyHumanConfirmation(message: Message): boolean {
  return getWorkflowStageGates(message).hasHumanConfirmation;
}

function hasPendingHumanConfirmation(message: Message): boolean {
  return getWorkflowStageGates(message).hasPendingHumanConfirmation;
}

function isHumanConfirmationResolved(message: Message): boolean {
  return getWorkflowStageGates(message).humanConfirmationResolved;
}

function hasRawPlanGenerationArtifact(message: Message): boolean {
  return Boolean(
    hasText(message.planGenerationReasoning)
      || hasText(message.stageBuffers?.planGeneration?.text)
      || Boolean(message.stageBuffers?.planGeneration?.artifact)
      || Boolean(message.stageBuffers?.planReview?.artifact)
      || hasText(message.planTransitionText)
      || message.planProcessSummary
      || hasPlanProcessStage(
        message,
        (stage, item) => stage === 'planning' && (hasText(item?.reasoning) || Boolean(item?.summary) || Boolean(item?.planReview)),
      )
      || latestPlanReview(message),
  );
}

function hasPlanGenerationArtifact(message: Message): boolean {
  const gates = getWorkflowStageGates(message);
  return gates.canEnterPlanGeneration && hasRawPlanGenerationArtifact(message);
}

function hasEngineeringReasoningArtifact(message: Message): boolean {
  return Boolean(message.stageBuffers?.planBasis?.started || getWorkflowStageGates(message).hasEngineeringReasoningArtifact === true);
}

/**
 * What the preflight found, as the preflight found it.
 *
 * Only the `validation` row reads this — its status, its summary and its detail
 * — so all three say the same thing about the same moment.
 *
 * 2026-09-15 线上：这一行运行时写「5 类根因（共 16 处）」，整轮结束后变成
 * 「6 类根因（共 19 处）」，预检一次都没有重跑。它读的是
 * `getEffectiveValidationReport`，那个函数优先返回 `repairAppendix.finalValidation`
 * ——修复循环的最新结果。对最终判定那个优先级是对的，对这一行不是：修复循环
 * 每改写一次校验结果，用户已经读过的预检行就被改写一次。
 *
 * `preflightReport` 是运行时在预检当时封存下来的那一份（见它在 types 里的注释）。
 * 早于这个字段的历史会话没有它，仍然按原来的顺序读。
 */
function getValidationReport(message: Message) {
  const sealed = message.preflightReport;
  if (sealed && typeof sealed === 'object' && !Array.isArray(sealed)) {
    return sealed as ReturnType<typeof getEffectiveValidationReport>;
  }

  const report = getEffectiveValidationReport(message);
  if (report) return report;

  const artifact = message.stageBuffers?.validation?.artifact;
  if (!artifact || typeof artifact !== 'object' || Array.isArray(artifact)) return undefined;
  const record = artifact as Record<string, unknown>;
  if (record.report && typeof record.report === 'object') return record.report;
  if ('valid' in record || 'issues' in record || 'errors' in record || 'warnings' in record) return record;
  return undefined;
}

function hasValidationArtifact(message: Message): boolean {
  return Boolean(getValidationReport(message) || message.stageBuffers?.validation?.artifact || message.parsed?.verification);
}

function hasCodeArtifact(message: Message): boolean {
  const repaired = getRepairDisplayStatus(message);
  return Boolean(message.parsed?.code && looksLikePlcCode(message.parsed.code))
    || Boolean(
      message.repairAppendix?.finalCode
        && looksLikePlcCode(message.repairAppendix.finalCode)
        && (repaired === 'completed' || repaired === 'completed_with_warnings'),
    );
}

function hasWorkflowStarted(message: Message): boolean {
  return Boolean(
    message.progressState
      || message.isStreaming
      || message.pipeline
      || message.planThinkingReasoning
      || message.stageBuffers
      || message.planAnalysisReasoning
      || message.planGenerationReasoning
      || message.planProcessSummary
      || message.planProcessHistory?.length
      || latestPlanReview(message)
      || hasCodeArtifact(message),
  );
}

/** A turn that is over: it said so, or it stopped streaming with a program. */
function isWorkflowFinished(message: Message): boolean {
  return message.progressState?.phase === 'completed' || (!message.isStreaming && hasCodeArtifact(message));
}

function getActiveStageKind(message: Message): StageKind | undefined {
  const progressState = message.progressState;
  const phase = progressState?.phase;
  const processStage = progressState?.stage;
  const planPhase = progressState?.planPhase;

  if (phase === 'failed' || processStage === 'failed') {
    if (message.stageBuffers?.repair?.status === 'failed') return 'repair';
    if (message.stageBuffers?.validation?.status === 'failed') return 'validation';
    if (message.stageBuffers?.codeGeneration?.status === 'failed') return 'code';
    if (message.stageBuffers?.planReview?.status === 'failed') return 'planReview';
    if (message.stageBuffers?.planGeneration?.status === 'failed') return 'planGeneration';
    if (message.stageBuffers?.planBasis?.status === 'failed') return 'engineeringReasoning';
    return undefined;
  }

  // Blocking gates must win over downstream active phases. SSE/cache data can
  // arrive out of order; the UI should still stop at the unresolved human/plan gate.
  if (hasPendingHumanConfirmation(message)) {
    return 'humanConfirmation';
  }

  if (hasBlockingPlanReview(message) && (phase === 'generating' || phase === 'explaining' || phase === 'finalizing' || phase === 'validating' || phase === 'repairing' || processStage === 'generation' || processStage === 'validation' || processStage === 'repair')) {
    return 'planReview';
  }

  if (message.stageBuffers?.repair?.status === 'streaming') {
    return 'repair';
  }

  if (getRepairDisplayStatus(message) === 'running') {
    return 'repair';
  }

  if (message.stageBuffers?.validation?.status === 'streaming') {
    return 'validation';
  }

  if (phase === 'validating' || processStage === 'validation') {
    return 'validation';
  }

  // Review (runtime batch, TA10a): the worker's post-repair frame is
  // `{ stage: 'final_checks', phase: 'finalizing' }` — ahead of the
  // `finalizing` check below, which would otherwise catch its `phase` and
  // leave the 生成代码 step reading as the active stage through the whole
  // final-checks window, with validation (what is actually running) never
  // highlighted at all.
  if (processStage === 'final_checks') {
    return 'validation';
  }

  if (message.stageBuffers?.codeGeneration?.status === 'streaming') {
    return 'code';
  }

  if (phase === 'generating' || phase === 'explaining' || phase === 'finalizing' || processStage === 'generation') {
    return 'code';
  }

  if (message.stageBuffers?.planReview?.artifact || message.stageBuffers?.planReview?.status === 'needs_approval') {
    return 'planReview';
  }

  if (message.stageBuffers?.planGeneration?.status === 'streaming') {
    return 'planGeneration';
  }

  if (message.stageBuffers?.planBasis?.status === 'streaming') {
    return 'engineeringReasoning';
  }

  if (message.stageBuffers?.requirementConfirmation?.status === 'streaming') {
    return hasAnyHumanConfirmation(message) ? 'humanConfirmation' : 'requirementConfirmation';
  }

  if (message.stageBuffers?.requirementAnalysis?.status === 'streaming') {
    return 'requirementAnalysis';
  }

  if (message.stageBuffers?.thinking?.status === 'streaming') {
    return 'requirementThinking';
  }

  if (phase === 'clarifying' || processStage === 'clarification') {
    return hasAnyHumanConfirmation(message) ? 'humanConfirmation' : 'requirementConfirmation';
  }

  if (phase === 'classifying' || processStage === 'requirement_analysis') {
    return 'requirementAnalysis';
  }

  if (
    phase === 'queued'
      || phase === 'dispatching'
      || processStage === 'routing'
      || (message.isStreaming && !hasRequirementAnalysisArtifact(message) && !hasRawPlanGenerationArtifact(message))
  ) {
    return 'requirementThinking';
  }

  const planReview = latestPlanReview(message);
  if (planReview && planReview.requiresApproval !== false && !planReview.reviewResult) {
    return 'planReview';
  }

  if (phase === 'planning' || processStage === 'planning') {
    const gates = getWorkflowStageGates(message);
    if (!planPhase || planPhase === 'analyzing' || planPhase === 'classifying') {
      return gates.canEnterEngineeringReasoning ? 'engineeringReasoning' : 'requirementAnalysis';
    }
    if (!hasRawPlanGenerationArtifact(message)) {
      return 'engineeringReasoning';
    }
    if (planPhase === 'done' && planReview && planReview.requiresApproval !== false && !planReview.reviewResult) {
      return 'planReview';
    }
    return 'planGeneration';
  }

  return undefined;
}

function stageStatusFromPlanReview(planReview: Message['planReview']): StageStatus {
  if (!planReview) return 'skipped';
  if (planReview.requiresApproval === false) return 'passed';
  if (planReview.reviewResult === 'approved' || planReview.reviewResult === 'modified') return 'passed';
  if (planReview.reviewResult === 'rejected') return 'failed';
  return 'needs_approval';
}

function isPlanApprovalResolvedOrNotRequired(message: Message): boolean {
  const planReview = latestPlanReview(message);
  if (!planReview) return true;
  if (planReview.requiresApproval === false) return true;
  return planReview.reviewResult === 'approved' || planReview.reviewResult === 'modified';
}

function hasBlockingPlanReview(message: Message): boolean {
  const planReview = latestPlanReview(message);
  return Boolean(
    planReview
      && planReview.requiresApproval !== false
      && !planReview.reviewResult,
  );
}

function statusForActiveStage(message: Message, kind: StageKind): StageStatus {
  if (kind === 'humanConfirmation') {
    return hasPendingHumanConfirmation(message) ? 'needs_input' : 'running';
  }
  if (kind === 'planReview') {
    return stageStatusFromPlanReview(latestPlanReview(message));
  }
  if (kind === 'repair') {
    const status = getRepairDisplayStatus(message);
    if (status === 'running') return 'running';
    if (status === 'completed' || status === 'completed_with_warnings') return 'repaired';
    if (status === 'failed') return 'failed';
    if (status === 'needs_review') return 'needs_approval';
    if (status === 'skipped') return 'skipped';
  }
  return 'running';
}

const NEVER_RAN: { status: StageStatus; summary: string } = { status: 'skipped', summary: '未执行' };

/**
 * The native compile row's status and summary, decided together so the two
 * cannot disagree about whether the compile ran.
 *
 * The row belongs to the turn's own compiler (`nativeCompileConnector`). TIA's
 * verdict is `tiaValidation`, the record the gate panel under the turn reads,
 * and it is worded by the same helpers. CODESYS's is read straight off the
 * stage the runtime writes: the compiler says what it said.
 *
 * A compile that never reached a verdict on a turn that is over did not run.
 * 2026-09-23 线上（TIA-UI-019）：Siemens 回合结束后这一行仍是
 * 「原生编译（CODESYS）· 等待中」——TIA 因目标项目未打开根本没有编译，而这一行
 * 既不认平台、不读 TIA 的结果，也不看回合有没有结束。
 *
 * @param t reads the one summary here that has a key; the others are Chinese,
 *   as every summary on this timeline is.
 */
function nativeCompileRow(
  message: Message,
  t: (key: string) => string = key => key,
): { status: StageStatus; summary?: string } {
  const finished = isWorkflowFinished(message);

  if (nativeCompileConnector(message) === 'tia') {
    const tia = message.tiaValidation;
    if (!tia || tia.reason === 'not_siemens_target') {
      return finished ? NEVER_RAN : { status: 'pending' };
    }
    // Chinese, as every summary on this timeline is.
    const blocker = readBlockerLabel(tia)?.[1];
    switch (readOutcome(tia)) {
      case 'passed':
        return { status: 'passed' };
      case 'failed':
        return { status: 'failed' };
      case 'waiting':
        // Waiting for an Agent to be ready: the engineer's to act on, and the
        // gate resumes the compile by itself once it is.
        return { status: 'needs_input', summary: blocker };
      default:
        return { status: 'skipped', summary: blocker || NEVER_RAN.summary };
    }
  }

  // The compiler's own words, last line first — the rows above it are the
  // poll saying what the Run was doing, and the last one is what it came to.
  const codesys = message.stageBuffers?.codesysValidation;
  const lines = String(codesys?.text || '')
    .split(/\r?\n/).map(line => line.trim()).filter(Boolean);
  const said = lines[lines.length - 1];
  // No stage on a finished turn says only that the turn's own run did not
  // compile. A compile the engineer starts from the page (`codesysRowSource`'s
  // 'hook') is never written back to the message, so 「未执行」 was false right
  // after they had watched one finish (review, 2026-09-23). Until those Runs are
  // kept on the turn, the row says what is always true.
  if (!codesys?.started) {
    return finished
      ? { status: 'skipped', summary: translateWithFallback(t, "Not compiled in this turn's run", '本轮流程未编译') }
      : { status: 'pending', summary: said };
  }
  if (codesys.status === 'streaming' || !codesys.done) {
    return finished ? NEVER_RAN : { status: 'running', summary: said };
  }
  if (codesys.status === 'skipped') return { status: 'skipped', summary: said };
  return { status: codesys.status === 'failed' ? 'failed' : 'passed', summary: said };
}

/**
 * What this stage is doing, before the turn's own liveness is taken into account.
 *
 * Split out from `deriveStageStatus` so the liveness rule below applies to every
 * branch at once rather than being repeated — and forgotten — in each.
 */
function deriveRawStageStatus(message: Message, kind: StageKind): StageStatus {
  const kindRank = STAGE_RANK[kind];
  const workflowStarted = hasWorkflowStarted(message);
  const workflowFinished = isWorkflowFinished(message);
  // A turn that is over is not sitting in a stage. The plan-review buffer keeps
  // its artifact after the review is approved, so the active stage still
  // resolves to planReview on a turn that went on to generate, validate and
  // finish — and every stage ranked after it is then held at 'pending'. That is
  // what leaves the execution record one step short for good, reading as a
  // conversation that stopped partway. A failed turn keeps its pointer: that is
  // genuinely where it stopped.
  const activeKind = workflowFinished && message.progressState?.phase !== 'failed'
    ? undefined
    : getActiveStageKind(message);
  const activeRank = activeKind ? STAGE_RANK[activeKind] : undefined;
  const planReview = latestPlanReview(message);
  const humanResolved = isHumanConfirmationResolved(message);
  const planApproved = isPlanApprovalResolvedOrNotRequired(message);

  if (activeKind === kind && message.progressState?.phase !== 'completed' && message.progressState?.phase !== 'failed') {
    return statusForActiveStage(message, kind);
  }

  switch (kind) {
    case 'requirementThinking':
      if (!workflowStarted) return 'pending';
      return activeKind === kind ? 'running' : 'passed';

    case 'requirementAnalysis':
      if (activeRank !== undefined && activeRank < kindRank) return 'pending';
      if (activeKind === kind) return 'running';
      if (activeRank !== undefined && activeRank > kindRank) return 'passed';
      if (hasRequirementAnalysisArtifact(message)) return 'passed';
      return workflowStarted ? 'pending' : 'skipped';

    case 'requirementConfirmation':
      if (activeRank !== undefined && activeRank < kindRank) return 'pending';
      if (activeKind === kind) return 'running';
      if (hasAnyHumanConfirmation(message)) return 'passed';
      if (activeRank !== undefined && activeRank > kindRank) return 'passed';
      if (hasRequirementAnalysisArtifact(message) || hasRawPlanGenerationArtifact(message) || hasCodeArtifact(message)) return 'passed';
      return workflowStarted ? 'pending' : 'skipped';

    case 'humanConfirmation':
      if (!hasAnyHumanConfirmation(message)) {
        if (activeRank !== undefined && activeRank < kindRank) return 'pending';
        return hasRawPlanGenerationArtifact(message) || hasCodeArtifact(message) || workflowFinished ? 'skipped' : 'pending';
      }
      if (hasPendingHumanConfirmation(message)) return 'needs_input';
      return 'passed';

    case 'engineeringReasoning':
      if (!humanResolved) return 'pending';
      if (activeRank !== undefined && activeRank < kindRank) return 'pending';
      if (activeKind === kind) return 'running';
      if (activeRank !== undefined && activeRank > kindRank) return 'passed';
      if (hasEngineeringReasoningArtifact(message) || hasPlanGenerationArtifact(message) || hasCodeArtifact(message)) return 'passed';
      return workflowFinished ? 'skipped' : 'pending';

    case 'planGeneration':
      if (!humanResolved) return 'pending';
      if (activeKind === 'planGeneration') return 'running';
      if (planReview || hasPlanGenerationArtifact(message)) return 'passed';
      if (activeRank !== undefined && activeRank < kindRank) return 'pending';
      // Code with no trace of planning anywhere is a turn that never planned — a
      // repair follow-up goes straight from the request to the program. Reading
      // 已完成 off the code alone claimed a stage that never ran.
      return workflowFinished || hasCodeArtifact(message) ? 'skipped' : 'pending';

    case 'planReview':
      if (!humanResolved) return 'pending';
      if (planReview) return stageStatusFromPlanReview(planReview);
      if (activeRank !== undefined && activeRank < kindRank) return 'pending';
      return hasCodeArtifact(message) || workflowFinished ? 'skipped' : 'pending';

    case 'code':
      if (message.stageBuffers?.codeGeneration?.status === 'failed') return 'failed';
      if (message.progressState?.phase === 'failed' && activeKind === kind) return 'failed';
      if (!humanResolved) return 'pending';
      if (!planApproved) return 'pending';
      if (hasCodeArtifact(message)) return 'passed';
      if (activeRank !== undefined && activeRank < kindRank) return 'pending';
      return workflowFinished ? 'skipped' : 'pending';

    case 'codesysValidation':
      return nativeCompileRow(message).status;

    case 'validation': {
      if (!humanResolved) return 'pending';
      if (!planApproved) return 'pending';
      if (message.stageBuffers?.validation?.status === 'streaming') return 'running';
      // A failed preflight that a later repair fixed is history: the row keeps
      // stating what the preflight found, in the colour of a fault that has
      // since been repaired rather than one still standing (see turnSteps).
      const report = getValidationReport(message);
      if (report) {
        if (report.valid !== false) return 'passed';
        return isRepairOutcomeSuccess(message) ? 'repaired' : 'failed';
      }
      if (message.stageBuffers?.validation?.status === 'completed') return 'passed';
      if (message.stageBuffers?.validation?.status === 'failed') {
        return isRepairOutcomeSuccess(message) ? 'repaired' : 'failed';
      }
      if (message.parsed?.verification) return 'passed';
      if (activeRank !== undefined && activeRank < kindRank) return 'pending';
      return hasCodeArtifact(message) || workflowFinished ? 'skipped' : 'pending';
    }

    case 'repair': {
      if (!humanResolved) return 'pending';
      if (!planApproved) return 'pending';
      const status = getRepairDisplayStatus(message);
      if (status === 'running') return 'running';
      if (status === 'completed' || status === 'completed_with_warnings') return 'repaired';
      if (status === 'failed') return 'failed';
      // Policy stopped it, so it is waiting on the engineer — not finished, and
      // emphatically not irrelevant. Ahead of the `skipped` case because the two
      // used to be the same branch, which is what produced 「自动修复已跳过」 on a
      // program that had failed validation.
      if (status === 'needs_review') return 'needs_approval';
      if (status === 'skipped') return 'skipped';
      if (message.stageBuffers?.repair?.status === 'streaming') return 'running';
      if (message.stageBuffers?.repair?.status === 'completed') return 'repaired';
      if (message.stageBuffers?.repair?.status === 'failed') return 'failed';
      if (message.pipeline?.repairHistory?.length) return 'repaired';
      if (activeRank !== undefined && activeRank < kindRank) return 'pending';
      return workflowFinished || hasValidationArtifact(message) ? 'skipped' : 'pending';
    }

    default:
      return 'skipped';
  }
}

/**
 * A stage that has not reported a terminal event is not necessarily still working.
 *
 * Some runtime paths return without closing the stage they were in — the
 * repair-disabled branch left `validation` at `streaming` on a turn it then
 * finished — so a spinner outlives the run that owns it. A turn that has stopped
 * streaming has stopped working, whatever its buffers still say; the stage did
 * not pass or fail either, so it is reported as neither. This mirrors the rule
 * `buildTurnSteps` already applies to the conversation stream, so the two
 * surfaces cannot disagree about whether the turn is over.
 */
function deriveStageStatus(message: Message, kind: StageKind): StageStatus {
  const status = deriveRawStageStatus(message, kind);
  return status === 'running' && !message.isStreaming ? 'pending' : status;
}

function translateWithFallback(t: (key: string) => string, key: string, fallbackZh: string): string {
  const translated = t(key);
  return translated && translated !== key ? translated : fallbackZh;
}

function getStageLabel(kind: StageKind, t: (key: string) => string): string {
  const labels: Record<StageKind, string> = {
    requirementThinking: translateWithFallback(t, 'Thinking', '思考'),
    requirementAnalysis: translateWithFallback(t, 'Requirement Analysis', '需求分析'),
    requirementConfirmation: translateWithFallback(t, 'Requirement Confirmation', '需求确认'),
    humanConfirmation: translateWithFallback(t, 'Human Confirmation', '人工确认'),
    engineeringReasoning: translateWithFallback(t, 'Engineering Reasoning', '计划依据'),
    planGeneration: translateWithFallback(t, 'Plan Generation', '计划'),
    planReview: translateWithFallback(t, 'Plan Review', '计划审批'),
    code: translateWithFallback(t, 'Code Generation', '生成代码'),
    validation: translateWithFallback(t, 'Validation', '校验'),
    repair: translateWithFallback(t, 'Repair', '修复'),
    // No vendor: the row is the turn's own compiler's, and on a Siemens turn
    // that is TIA Portal (用户 2026-09-23：只写「原生编译」四个字).
    codesysValidation: translateWithFallback(t, 'Native compile', '原生编译'),
  };
  return labels[kind];
}

function getHumanConfirmationSummary(message: Message): string | undefined {
  const gates = getWorkflowStageGates(message);
  const totalRequired = gates.requiredHumanConfirmationCount;
  if (!gates.hasHumanConfirmation) return undefined;
  if (gates.hasPendingHumanConfirmation) {
    if (totalRequired > 0) {
      return `${gates.answeredRequiredHumanConfirmationCount}/${totalRequired} 个必填项已确认`;
    }
    return `${gates.pendingHumanConfirmationCount} 项待确认`;
  }
  return '已确认';
}

/**
 * What requirement analysis extracted, stated as counts.
 *
 * Shared with the turn's step stream rather than derived twice: on a reloaded
 * session that stream builds its rows out of this very summary, so the two
 * drifting apart would make one turn read differently before and after a
 * refresh. It lives here because the step stream already imports this module
 * and the dependency cannot run the other way.
 *
 * Neither surface may fall back to the normalized prompt, which both did
 * whenever no I/O points were found. That is the user's own words read back at
 * them — never a conclusion — and once the clarification answers are appended
 * to the prompt it runs far past what either can show: the step stream cut it
 * at 120 characters and marked the cut, this one cut it at 30 and did not.
 * Counting makes the overflow impossible rather than unlikely.
 *
 * The I/O count alone was not enough to decide on. Those points are found by a
 * regex over the prompt looking for hardware addresses and code identifiers, so
 * a requirement written as Chinese prose yields none at all while every other
 * field here — extracted with language-aware rules — is populated. Reading only
 * that one count is what sent those turns down the echo path.
 *
 * @param t how the reader's language says each fragment. Absent — as it is for
 *   every summary on this timeline — the sentence reads Chinese.
 */
export function requirementAnalysisConclusion(
  message: Message,
  t?: (key: string) => string,
): string | undefined {
  const requirement = message.pipeline?.requirement;
  if (!requirement) return undefined;

  const say = (key: string, zh: string) => (t ? t(key) : zh);
  const sayCount = (key: string, zh: string, count: number) =>
    say(key, zh).replace('{count}', String(count));

  /** Names, trimmed, and never more than a few: a list that grows with the
   *  requirement stops being a summary. */
  const names = (values: unknown[], limit: number): string[] => values
    .map(item => (typeof item === 'string'
      ? item
      : ((item as any)?.title
        || (item as any)?.name
        || (item as any)?.address
        || (item as any)?.signal
        || (item as any)?.description
        || '')).trim())
    .filter(Boolean)
    .slice(0, limit)
    .map(item => (item.length > 24 ? `${item.slice(0, 24)}…` : item));

  const join = (items: string[]) => items.join(say(', ', '、'));

  // Say what was found; fall back to how much only when there is no what.
  //
  // 「已识别 2 个设备、3 项安全约束、3 项关键条件。 急停优先级; 传感器故障处理.」
  // was the whole of what a finished requirement analysis said on 2026-09-08.
  // Which two devices, and which three conditions, were nowhere — and two bare
  // safety titles trailing after a full stop read as debris rather than as a
  // sentence. A count is the one thing the reader cannot check; a name is what
  // they catch a misreading in. Unnamed items still exist, though, and going
  // silent about them says less than counting them did.
  const part = (
    values: unknown[],
    nameEn: string,
    nameZh: string,
    countEn: string,
    countZh: string,
    limit: number,
  ): string | undefined => {
    const named = names(values, limit);
    if (named.length) return say(nameEn, nameZh).replace('{items}', join(named));
    if (values.length) return sayCount(countEn, countZh, values.length);
    return undefined;
  };

  const parts = [
    part(requirement.entities || [], 'devices: {items}', '设备：{items}', '{count} devices', '{count} 个设备', 3),
    part(
      [...(requirement.ioSignals || []), ...(requirement.ioPoints || [])],
      'I/O: {items}', 'I/O：{items}', '{count} I/O points', '{count} 个 I/O 点', 4,
    ),
    // Two, because the safety list is the one that grows fastest.
    part(requirement.safetyConstraints || [], 'safety: {items}', '安全约束：{items}', '{count} safety constraints', '{count} 项安全约束', 2),
    part(requirement.keyConditions || [], 'conditions: {items}', '关键条件：{items}', '{count} key conditions', '{count} 项关键条件', 3),
  ].filter(Boolean) as string[];

  if (!parts.length) return undefined;

  const identified = say('Identified {items}.', '已识别 {items}。')
    .replace('{items}', parts.join(say('; ', '；')));

  // What is still open matters more than what was found: it is what the cards
  // underneath are about to ask.
  const missing = names(requirement.missingInformation || [], 2);
  if (!missing.length) return identified;

  // Only a turn that is asking has cards underneath to point at. 2026-09-23
  // 线上（TIA-UI-010）：确认之后的下一轮，第二个「需求分析」仍写着「……已按保守
  // 默认假设，可在下方确认中调整。」，下方一张卡片都没有。
  const open = (hasPendingHumanConfirmation(message)
    ? say(
      'Still open: {items} — assumed conservatively in the cards below.',
      '尚未给出：{items}，已按保守默认假设，可在下方确认中调整。',
    )
    : say('Still open: {items} — assumed conservatively.', '尚未给出：{items}，已按保守默认假设。')
  ).replace('{items}', join(missing));

  return `${identified}${open}`;
}

function getStageSummary(message: Message, kind: StageKind, t: (key: string) => string): string | undefined {
  const planReview = latestPlanReview(message);

  switch (kind) {
    case 'requirementThinking':
      return message.progressState?.label;
    case 'requirementAnalysis':
      return requirementAnalysisConclusion(message);
    case 'requirementConfirmation': {
      const count = (message.pipeline?.requirement?.ioPoints?.length || 0)
        + (message.pipeline?.requirement?.ioSignals?.length || 0);
      if (count > 0) return `${count} I/O signals confirmed`;
      if (hasAnyHumanConfirmation(message)) return '确认项已生成';
      return undefined;
    }
    case 'humanConfirmation':
      return getHumanConfirmationSummary(message) || '不需要人工确认';
    case 'engineeringReasoning':
      return undefined;
    case 'planGeneration': {
      if (!isHumanConfirmationResolved(message)) return undefined;
      if (message.planProcessSummary) return `${message.planProcessSummary.subtaskCount} 个实施项`;
      // Said by the plan stage's own text, not by the message's `reasoning`: that is the same prose again, and the
      // gateway does not send it to someone who may not read model diagnostics (streamedTurnWithoutReasoning.test.ts).
      if (hasText(message.stageBuffers?.planGeneration?.text)) return '计划依据已生成';
      return '正在生成计划依据';
    }
    case 'planReview':
      if (!isHumanConfirmationResolved(message)) return undefined;
      if (!planReview) return undefined;
      if (planReview.requiresApproval === false) return '计划无需人工审批';
      if (!planReview.reviewResult) return '等待计划确认';
      if (planReview.reviewResult === 'approved') return '计划已通过';
      if (planReview.reviewResult === 'modified') return '计划已修改后通过';
      if (planReview.reviewResult === 'rejected') return '计划被拒绝';
      if (planReview.reviewResult === 'regenerate_subtask') return '正在重新规划子任务';
      return planReview.requirementSummary;
    case 'code':
      if (!isHumanConfirmationResolved(message) || !isPlanApprovalResolvedOrNotRequired(message)) return undefined;
      return message.parsed?.code
        ? `${getDisplayCodeLineCount(message.parsed.code)} 行`
        : undefined;
    case 'codesysValidation':
      return nativeCompileRow(message, t).summary;

    case 'validation': {
      if (!isHumanConfirmationResolved(message) || !isPlanApprovalResolvedOrNotRequired(message)) return undefined;
      const report = getValidationReport(message);
      if (!report && !message.parsed?.verification && !message.stageBuffers?.validation?.artifact) return undefined;
      return report
        ? formatValidationRootCauseCount(report, message.generationMetrics?.reviewRequired, t)
        : deliveryValidationLabel('校验通过', message.generationMetrics?.reviewRequired, t);
    }
    case 'repair':
      if (!isHumanConfirmationResolved(message) || !isPlanApprovalResolvedOrNotRequired(message)) return undefined;
      {
        const repairStatus = getRepairDisplayStatus(message);
        const repairSummary = getRepairTimelineSummary(repairStatus, message.generationMetrics?.reviewRequired, t);
        // `isRepairAppendixAuthoritative`: a terminal `repairStatus` can now
        // come from the stage buffer alone, ahead of `repairAppendix` itself
        // saying anything (TA10b review #3) — in that window the generic
        // sentence is not yet earned, and the appendix's own summary (or the
        // buffer's text) below is the more current thing to show.
        if (
          (repairStatus === 'completed' || repairStatus === 'completed_with_warnings' || repairStatus === 'failed')
          && isRepairAppendixAuthoritative(message)
        ) {
          return repairSummary;
        }
        // I-3 (review of 8305b621): not scoped to any particular `repairStatus`
        // — a live repair loop (`repairStatus === 'running'`) already has its
        // own round-by-round summary in the appendix just as much as a
        // buffer-settled one does, and it is more current than
        // `stageBuffers.repair.text`: that buffer keeps growing every round,
        // but `compactProjectTitle` below only ever reads its first line, cut
        // to 30 characters — frozen at whatever the first round happened to
        // open with. `needs_review` and `skipped` are themselves terminal
        // appendix statuses (`isRepairAppendixAuthoritative` is true for
        // both), so they still keep their own curated sentence below.
        if (!isRepairAppendixAuthoritative(message) && hasText(message.repairAppendix?.summary)) {
          return message.repairAppendix?.summary;
        }
        // The repair stage streams the model's own reasoning notes into
        // `text`, the same channel every other stage uses for reasoning (see
        // `turnSteps.ts`'s comment on this same field) — not a written
        // summary. Read here only as a last resort, when neither the appendix
        // nor the generic sentence above has anything to show yet.
        if (message.stageBuffers?.repair?.text) return compactProjectTitle(message.stageBuffers.repair.text);
        return repairSummary;
      }
    default:
      return undefined;
  }
}

const MAX_DETAIL_CHARS = 20_000;

function stripThinkTags(text: string): string {
  return text.replace(/<think>[\s\S]*?(?:<\/think>|$)/gi, '').trim();
}

function asDetailText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const text = stripThinkTags(value);
  if (!text) return undefined;
  return text.length > MAX_DETAIL_CHARS ? `${text.slice(0, MAX_DETAIL_CHARS)}…` : text;
}

function formatConfirmedAnswersDetail(message: Message): string | undefined {
  const answers = message.clarifyState?.confirmedAnswers;
  if (!answers?.length) return undefined;
  return answers
    .map(answer => `• ${answer.question}\n  → ${answer.selectedOptions.join('、') || '(未选择)'}`)
    .join('\n');
}

function formatPlanReviewDetail(message: Message): string | undefined {
  const planReview = latestPlanReview(message);
  if (!planReview) return undefined;
  const parts: string[] = [];
  if (hasText(planReview.requirementSummary)) parts.push(planReview.requirementSummary.trim());
  if (planReview.reviewResult === 'approved') parts.push('结论:计划已通过');
  else if (planReview.reviewResult === 'modified') parts.push('结论:计划已修改后通过');
  else if (planReview.reviewResult === 'rejected') parts.push('结论:计划被拒绝');
  else if (planReview.requiresApproval === false) parts.push('结论:计划无需人工审批');
  return parts.length ? parts.join('\n\n') : undefined;
}

function formatValidationDetail(message: Message): string | undefined {
  const bufferText = asDetailText(message.stageBuffers?.validation?.text);
  if (bufferText) return bufferText;
  const report = getValidationReport(message);
  if (!report) return asDetailText(message.parsed?.verification);
  try {
    return asDetailText(JSON.stringify(report, null, 2));
  } catch {
    return undefined;
  }
}

/**
 * P0-1: full stage content, preserved after completion and reload.
 * Sources are ordered: streaming stage buffers (richest) → persisted
 * per-stage reasoning fields → structured artifacts.
 */
export function getStageDetail(
  message: Message,
  kind: StageKind,
): { text: string; kind: 'code' | 'text' } | undefined {
  switch (kind) {
    case 'requirementThinking': {
      const text = asDetailText(message.stageBuffers?.thinking?.text)
        || asDetailText(message.planThinkingReasoning);
      return text ? { text, kind: 'text' } : undefined;
    }
    case 'requirementAnalysis': {
      const text = asDetailText(message.stageBuffers?.requirementAnalysis?.text)
        || asDetailText(message.planAnalysisReasoning)
        || asDetailText(message.pipeline?.requirement?.normalizedPrompt);
      return text ? { text, kind: 'text' } : undefined;
    }
    case 'humanConfirmation': {
      const text = formatConfirmedAnswersDetail(message);
      return text ? { text, kind: 'text' } : undefined;
    }
    case 'engineeringReasoning': {
      const text = asDetailText(message.stageBuffers?.planBasis?.text)
        || asDetailText(message.reasoning);
      return text ? { text, kind: 'text' } : undefined;
    }
    case 'planGeneration': {
      const text = asDetailText(message.stageBuffers?.planGeneration?.text)
        || asDetailText(message.planGenerationReasoning);
      return text ? { text, kind: 'text' } : undefined;
    }
    case 'planReview': {
      const text = formatPlanReviewDetail(message);
      return text ? { text, kind: 'text' } : undefined;
    }
    case 'code': {
      const text = asDetailText(message.parsed?.code);
      return text ? { text, kind: 'code' } : undefined;
    }
    case 'validation': {
      const text = formatValidationDetail(message);
      return text ? { text, kind: 'text' } : undefined;
    }
    case 'repair': {
      const text = asDetailText(message.stageBuffers?.repair?.text);
      return text ? { text, kind: 'text' } : undefined;
    }
    default:
      return undefined;
  }
}

function getStageMeta(message: Message, kind: StageKind, t: (key: string) => string): string | undefined {
  if (kind !== 'planReview') return undefined;
  const planReview = latestPlanReview(message);
  if (!planReview) return undefined;

  const source = planReview.approvalSource;
  const approved = planReview.reviewResult === 'approved' || planReview.reviewResult === 'modified';
  const awaitingApproval = planReview.requiresApproval !== false && !planReview.reviewResult;
  if (source === 'manual' && approved) {
    return translateWithFallback(t, 'Manual approval', '人工批准');
  }
  if ((source === 'auto' && approved) || planReview.requiresApproval === false) {
    return translateWithFallback(t, 'Auto approval', '自动批准');
  }
  if (awaitingApproval) {
    return translateWithFallback(t, 'Awaiting approval', '等待审批');
  }
  return undefined;
}

function isSafetyStage(kind: StageKind, message: Message): boolean {
  return (kind === 'validation' || kind === 'repair') && !!message.strictSafetyCheck;
}

export function buildStageTimelineItems(
  message: Message,
  displayStrategy: DisplayStrategy,
  t: (key: string) => string,
): StageTimelineItem[] {
  // A target RealPLC has no native compiler for has no such step: its row
  // could only ever say it was waiting.
  const hasNativeCompile = nativeCompileConnector(message) !== null;
  return STAGE_ORDER
    .filter(kind => displayStrategy.stages[kind])
    .filter(kind => kind !== 'codesysValidation' || hasNativeCompile)
    .map(kind => {
      const detail = getStageDetail(message, kind);
      return {
        id: `${message.id}-${kind}`,
        kind,
        label: getStageLabel(kind, t),
        status: deriveStageStatus(message, kind),
        summary: getStageSummary(message, kind, t),
        detail: detail?.text,
        detailKind: detail?.kind,
        meta: getStageMeta(message, kind, t),
        isSafetyRelated: isSafetyStage(kind, message),
        reviewRequired: kind === 'validation' ? message.generationMetrics?.reviewRequired : undefined,
      };
    });
}
